import { useState, useEffect, useRef } from 'react';

interface UseScheduleViewTimerProps {
  scheduleIndex: number;
  onShowSavePrompt: () => void;
  thresholdMs?: number;
}

export const useScheduleViewTimer = ({ 
  scheduleIndex, 
  onShowSavePrompt, 
  thresholdMs = 5000 
}: UseScheduleViewTimerProps) => {
  const [hasShownPrompt, setHasShownPrompt] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastScheduleIndexRef = useRef<number>(scheduleIndex);

  useEffect(() => {
    // Reset timer when schedule index changes
    if (lastScheduleIndexRef.current !== scheduleIndex) {
      lastScheduleIndexRef.current = scheduleIndex;
      setHasShownPrompt(false);
      
      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    // Start timer if we haven't shown prompt for this schedule yet
    if (!hasShownPrompt) {
      timerRef.current = setTimeout(() => {
        onShowSavePrompt();
        setHasShownPrompt(true);
      }, thresholdMs);
    }

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [scheduleIndex, hasShownPrompt, onShowSavePrompt, thresholdMs]);

  // Reset the timer state (useful for testing or manual reset)
  const resetTimer = () => {
    setHasShownPrompt(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    hasShownPrompt,
    resetTimer
  };
};
